import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faGift,
    faBoxOpen,
    faFilter,
    faLayerGroup,
    faList,
    faGrip,
    faSearch,
    faPlus,
    faEyeSlash,
} from '@fortawesome/free-solid-svg-icons';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useCollectionData } from '../../hooks/useCollectionData';
import { useFilterSort } from '../../hooks/useFilterSort';
import GuestLanding from '../GuestLanding';

import { ConfirmationDialog, BulkRemoveConfirmDialog } from './ConfirmationDialog';
import SetStatusModal from './SetStatusModal';
import ProfileHeader from './ProfileHeader';
import StatsCards from './StatsCards';
import FilterPanel, { PriceToggle } from './FilterPanel';
import CollectionSetCard from './CollectionSetCard';
import ThemeGroup from './ThemeGroup';
import { SortHeader, ListRow } from './ListViewRow';
import BulkToolbar from './BulkToolbar';

// ─────────────────────────────────────────────────────────────────────────────
// Helper: decode the username URL param safely
// ─────────────────────────────────────────────────────────────────────────────
const decodeSlug = (param) => {
    if (param == null || param === '') return '';
    try { return decodeURIComponent(param); } catch { return param; }
};

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
const UserSetsView = () => {
    const { username: usernameParam } = useParams();
    const slug = decodeSlug(usernameParam);
    const { user } = useAuth();
    const location = useLocation();

    const isWishlist = location.pathname.startsWith('/wishlist');
    const isOwner = !!(user && slug && user.username?.toLowerCase() === slug.toLowerCase());
    const ownerUserId = isOwner && user ? Number(user.user_id) : null;

    // ── Persistent UI preferences ─────────────────────────────────────────
    const [showPrices, setShowPrices] = useState(() => {
        const v = localStorage.getItem('showPrices');
        return v !== null ? JSON.parse(v) : false;
    });
    const [isListView, setIsListView] = useState(() => {
        const v = localStorage.getItem('isListView');
        return v !== null ? JSON.parse(v) : false;
    });

    useEffect(() => { localStorage.setItem('showPrices', JSON.stringify(showPrices)); }, [showPrices]);
    useEffect(() => { localStorage.setItem('isListView', JSON.stringify(isListView)); }, [isListView]);

    // ── Transient UI state ────────────────────────────────────────────────
    const [selectedSet, setSelectedSet] = useState(null);
    const [collapsedThemes, setCollapsedThemes] = useState({});
    const [copiedUrl, setCopiedUrl] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, set: null });
    const [statusModal, setStatusModal]       = useState({ isOpen: false, set: null });
    const [isBulkMode, setIsBulkMode]         = useState(false);
    const [selectedSets, setSelectedSets]     = useState(new Set());
    const [isBulkRemoving, setIsBulkRemoving] = useState(false);
    const [isBulkOpening, setIsBulkOpening]   = useState(false);
    const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

    // ── Data & filter hooks ───────────────────────────────────────────────
    const {
        sets, setSets, profileData, trophies,
        isLoading, isPrivateProfile, userExists,
        updateQuantity, updateCompleteCount, updateSealedCount,
        removeSet, moveToCollection, bulkRemove, bulkOpen,
    } = useCollectionData({ slug, isWishlist, ownerUserId });

    const {
        filteredSets,
        searchQuery, setSearchQuery,
        sortOption, setSortOption,
        filterOptions, setFilterOptions,
        listSortColumn, listSortDir,
        handleSortChange, getSortIcon,
        resetFilters, getDecadeOptions,
        groupAndSortThemes,
        handleListSort, sortSetsForList,
    } = useFilterSort({ sets, isWishlist });

    // ── Document title ────────────────────────────────────────────────────
    const pageTitle = useMemo(() => {
        const suffix = isWishlist ? 'Wishlist' : 'Collection';
        if (!slug)        return `My ${suffix}`;
        if (isLoading)    return suffix;
        if (!userExists)  return 'User not found';
        if (isOwner)      return `My ${suffix}`;
        const name = profileData?.display_name || profileData?.username || 'User';
        return `${name}'s ${suffix}`;
    }, [slug, isWishlist, isLoading, userExists, isOwner, profileData]);

    const pageDescription = useMemo(() => {
        if (slug) return null;
        return isWishlist
            ? 'Create a free LEGO® wishlist on MyBrickLog. Track sets you want to buy, share with family for gift ideas, and monitor aftermarket prices for retired sets. 25,000+ sets in the database.'
            : 'Track your entire LEGO® collection for free on MyBrickLog. Log every set you own, mark sealed and opened copies, track minifigures, and check current market values for retired sets.';
    }, [slug, isWishlist]);

    useDocumentTitle(pageTitle, pageDescription);

    // ── Handlers ──────────────────────────────────────────────────────────
    const shareUrl = () => {
        navigator.clipboard.writeText(window.location.href).then(() => {
            setCopiedUrl(true);
            setTimeout(() => setCopiedUrl(false), 2500);
        });
    };

    const handleRemoveClick = (set, e) => {
        e.stopPropagation();
        setConfirmDialog({ isOpen: true, set });
    };

    const handleConfirmRemove = async () => {
        if (!confirmDialog.set) return;
        await removeSet(confirmDialog.set.set_num);
        setConfirmDialog({ isOpen: false, set: null });
    };

    const openStatusModal  = (set) => setStatusModal({ isOpen: true,  set });
    const closeStatusModal = ()    => setStatusModal({ isOpen: false, set: null });

    const toggleCollapse = (themeId) =>
        setCollapsedThemes(prev => ({ ...prev, [themeId]: !prev[themeId] }));

    const toggleSelectSet = (set_num) =>
        setSelectedSet(prev => prev === set_num ? null : set_num);

    const toggleBulkMode = () => {
        setIsBulkMode(prev => !prev);
        setSelectedSets(new Set());
        setSelectedSet(null);
    };

    const toggleBulkSelect = (set_num) =>
        setSelectedSets(prev => {
            const next = new Set(prev);
            next.has(set_num) ? next.delete(set_num) : next.add(set_num);
            return next;
        });

    const selectAllVisible = () => setSelectedSets(new Set(filteredSets.map(s => s.set_num)));
    const deselectAll      = () => setSelectedSets(new Set());

    const handleBulkOpen = async () => {
        if (selectedSets.size === 0) return;
        setIsBulkOpening(true);
        try { await bulkOpen([...selectedSets]); } catch (err) { console.error(err); }
        finally { setIsBulkOpening(false); }
    };

    const handleBulkRemove = async () => {
        if (selectedSets.size === 0) return;
        setIsBulkRemoving(true);
        try {
            await bulkRemove([...selectedSets]);
            setSelectedSets(new Set());
            setBulkConfirmOpen(false);
        } catch (err) { console.error(err); }
        finally { setIsBulkRemoving(false); }
    };

    const switchToListView = () => {
        setIsListView(true);
        setSortOption('theme');  // list view always groups by theme
    };

    // ── Shared card props (avoids re-listing at every call site) ──────────
    const sharedCardProps = {
        isOwner, isBulkMode, selectedSets, toggleBulkSelect,
        selectedSet, toggleSelectSet, showPrices, isWishlist,
        openStatusModal, handleRemoveClick, moveToCollection,
    };

    // ── Render helpers ────────────────────────────────────────────────────
    const renderThemeView = (themesData) => {
        if (!themesData) return null;

        const fullWidth  = themesData.filter(t =>  t.isFullWidth);
        const halfWidth  = themesData.filter(t => !t.isFullWidth);
        const rows = [];
        for (let i = 0; i < halfWidth.length; i += 2) rows.push(halfWidth.slice(i, i + 2));

        return (
            <div className="flex flex-col gap-4 sm:gap-6">
                {fullWidth.map(theme => (
                    <ThemeGroup key={theme.theme_id} theme={theme} collapsedThemes={collapsedThemes} onToggle={toggleCollapse} className="w-full">
                        <div className="p-3 sm:p-4 md:p-6">
                            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-4">
                                {theme.sets.map(set => <CollectionSetCard key={set.set_num} set={set} {...sharedCardProps} />)}
                            </div>
                        </div>
                    </ThemeGroup>
                ))}

                {rows.map((row, i) => (
                    <div key={`row-${i}`} className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 items-start">
                        {row.map(theme => (
                            <ThemeGroup key={theme.theme_id} theme={theme} collapsedThemes={collapsedThemes} onToggle={toggleCollapse}>
                                <div className="p-3 sm:p-4 md:p-6">
                                    <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-4">
                                        {theme.sets.map(set => <CollectionSetCard key={set.set_num} set={set} {...sharedCardProps} />)}
                                    </div>
                                </div>
                            </ThemeGroup>
                        ))}
                    </div>
                ))}
            </div>
        );
    };

    const renderGridView = (setsToRender) => (
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
            {setsToRender.map(set => <CollectionSetCard key={set.set_num} set={set} {...sharedCardProps} />)}
        </div>
    );

    const renderListView = (themesData) => {
        if (!themesData) return null;

        const hasMinifigures = filteredSets.some(s => Number(s.num_minifigures) > 0);

        const sharedRowProps = {
            isOwner, isBulkMode, selectedSets, selectedSet,
            toggleBulkSelect, toggleSelectSet, isWishlist,
            showPrices, hasMinifigures,
            moveToCollection, handleRemoveClick, openStatusModal,
        };

        const sortHeaderProps = { listSortColumn, listSortDir, onSort: handleListSort };

        return (
            <div className="flex flex-col gap-4 sm:gap-6">
                {themesData.map(theme => (
                    <ThemeGroup key={theme.theme_id} theme={theme} collapsedThemes={collapsedThemes} onToggle={toggleCollapse}>
                        <div className="overflow-x-auto">
                            <table className="w-full table-auto">
                                <thead>
                                    <tr className="bg-gray-50 border-b-2 border-gray-200">
                                        {isBulkMode && isOwner && <th className="w-10 px-3 py-3" />}
                                        <th className="w-14 px-2 py-3" />
                                        <SortHeader column="set_num" label="Set #"  {...sortHeaderProps} />
                                        <SortHeader column="name"    label="Name"   className="min-w-[160px]" {...sortHeaderProps} />
                                        <SortHeader column="year"    label="Year"   centerText {...sortHeaderProps} />
                                        <SortHeader column="num_parts" label="Pieces" centerText {...sortHeaderProps} />
                                        {hasMinifigures && (
                                            <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-center whitespace-nowrap">Figs</th>
                                        )}
                                        {!isWishlist && (
                                            <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Status</th>
                                        )}
                                        {showPrices && (
                                            <>
                                                <SortHeader column="retail_price" label="Retail"   centerText {...sortHeaderProps} />
                                                <SortHeader column="sealed_value" label="Sealed $" centerText {...sortHeaderProps} />
                                                <SortHeader column="used_value"   label="Used $"   centerText {...sortHeaderProps} />
                                            </>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortSetsForList(theme.sets).map(set => (
                                        <ListRow key={set.set_num} set={set} {...sharedRowProps} />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </ThemeGroup>
                ))}
            </div>
        );
    };

    const renderEmptyState = () => {
        const hasActiveFilters = searchQuery || filterOptions.complete !== 'all' || filterOptions.sealed !== 'all' || filterOptions.decade !== 'all';

        if (hasActiveFilters) {
            return (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <FontAwesomeIcon icon={faSearch} className="text-gray-400 text-xl" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">No sets found</h3>
                    <p className="text-gray-600 mb-6">Try adjusting your filters or search terms.</p>
                    <button onClick={resetFilters} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                        Reset Filters
                    </button>
                </div>
            );
        }

        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FontAwesomeIcon icon={isWishlist ? faGift : faBoxOpen} className="text-gray-400 text-xl" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    {isOwner
                        ? `You haven't added any sets to your ${isWishlist ? 'wishlist' : 'collection'} yet.`
                        : `This user hasn't added any sets to their ${isWishlist ? 'wishlist' : 'collection'} yet.`
                    }
                </h3>
                {isOwner && (
                    <div className="mt-6">
                        <Link to="/themes" className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium">
                            <FontAwesomeIcon icon={faPlus} className="mr-2" />
                            {`Add Sets to Your ${isWishlist ? 'Wishlist' : 'Collection'}`}
                        </Link>
                    </div>
                )}
            </div>
        );
    };

    // ── Early exits ───────────────────────────────────────────────────────
    if (!slug) return <GuestLanding isWishlist={isWishlist} />;

    if (isLoading) {
        return (
            <div className="max-w-7xl mx-auto px-4 py-16 text-center">
                <div className="w-16 h-16 simple-spinner mx-auto" />
                <p className="mt-4 text-gray-600">Loading {isWishlist ? 'wishlist' : 'collection'}...</p>
            </div>
        );
    }

    if (!userExists) {
        return (
            <div className="max-w-7xl mx-auto px-4 py-16 text-center">
                <div className="bg-red-100 border border-red-200 text-red-700 px-6 py-4 rounded-xl">
                    <p className="font-medium">User not found</p>
                    <p className="text-sm mt-1">The user you're looking for doesn't exist or has been removed.</p>
                </div>
            </div>
        );
    }

    if (isPrivateProfile) {
        return (
            <div className="max-w-7xl mx-auto px-4 py-16 text-center">
                <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-8 max-w-xl mx-auto">
                    <div className="w-14 h-14 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                        <FontAwesomeIcon icon={faEyeSlash} className="text-gray-500 text-xl" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Profile is private</h2>
                    <p className="text-gray-600">This user&apos;s profile is private.</p>
                </div>
            </div>
        );
    }

    const themesData = (sortOption === 'theme' || isListView)
        ? groupAndSortThemes(filteredSets, profileData?.favorite_theme)
        : null;

    const hasActiveFilters = searchQuery || filterOptions.complete !== 'all' || filterOptions.sealed !== 'all' || filterOptions.decade !== 'all';
    const activeFilterCount =
        (filterOptions.complete !== 'all' ? 1 : 0) +
        (filterOptions.sealed   !== 'all' ? 1 : 0) +
        (filterOptions.decade   !== 'all' ? 1 : 0) +
        (searchQuery ? 1 : 0);

    // ── Main render ───────────────────────────────────────────────────────
    return (
        <div className={`w-full mx-auto px-2 sm:px-4 py-4 sm:py-8 ${isBulkMode ? 'pb-28' : ''}`}>
            {/* Dialogs / modals */}
            <ConfirmationDialog
                isOpen={confirmDialog.isOpen}
                onClose={() => setConfirmDialog({ isOpen: false, set: null })}
                onConfirm={handleConfirmRemove}
                set={confirmDialog.set}
                isWishlist={isWishlist}
            />
            <SetStatusModal
                isOpen={statusModal.isOpen}
                onClose={closeStatusModal}
                set={statusModal.set}
                onUpdateQuantity={updateQuantity}
                onUpdateComplete={updateCompleteCount}
                onUpdateSealed={updateSealedCount}
                setSets={setSets}
                onRemove={async () => {
                    await removeSet(statusModal.set.set_num);
                    closeStatusModal();
                }}
            />
            <BulkRemoveConfirmDialog
                isOpen={bulkConfirmOpen}
                count={selectedSets.size}
                onClose={() => setBulkConfirmOpen(false)}
                onConfirm={handleBulkRemove}
                isRemoving={isBulkRemoving}
            />

            <div className="max-w-7xl mx-auto">
                {/* Profile header */}
                <ProfileHeader
                    profileData={profileData}
                    trophies={trophies}
                    slug={slug}
                    isWishlist={isWishlist}
                    isOwner={isOwner}
                    copiedUrl={copiedUrl}
                    onShare={shareUrl}
                />

                {/* Section heading + toolbar */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
                    <h2 className="text-3xl font-bold text-gray-800 flex items-center">
                        <FontAwesomeIcon icon={isWishlist ? faGift : faBoxOpen} className="mr-3 text-red-600" />
                        {isOwner
                            ? `My ${isWishlist ? 'Wishlist' : 'Collection'}`
                            : `${profileData?.display_name || profileData?.username}'s ${isWishlist ? 'Wishlist' : 'Collection'}`
                        }
                    </h2>

                    {sets.length > 0 && (
                        <div className="flex flex-wrap items-center gap-3">
                            {/* Price toggle */}
                            <PriceToggle showPrices={showPrices} onToggle={() => setShowPrices(p => !p)} />

                            {/* Filter toggle */}
                            <button
                                onClick={() => setShowFilters(p => !p)}
                                className={`flex items-center gap-3 px-5 py-2.5 rounded-xl transition-all duration-200 font-semibold shadow-md hover:shadow-lg border ${
                                    showFilters
                                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white border-blue-500 hover:scale-105'
                                        : hasActiveFilters
                                            ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white border-red-500 hover:scale-105'
                                            : 'bg-gradient-to-r from-white to-gray-50 hover:from-gray-50 hover:to-gray-100 text-gray-700 border-gray-300 hover:scale-105'
                                }`}
                            >
                                <FontAwesomeIcon
                                    icon={faFilter}
                                    className={`text-sm ${
                                        showFilters    ? 'text-blue-100' :
                                        hasActiveFilters ? 'text-red-100' : 'text-gray-500'
                                    }`}
                                />
                                <span>Filters</span>
                                {hasActiveFilters && (
                                    <div className={`w-6 h-6 bg-opacity-40 rounded-full text-xs font-bold flex items-center justify-center ring-2 ring-opacity-50 ${
                                        showFilters
                                            ? 'bg-blue-400 text-blue-100 ring-blue-300'
                                            : 'bg-red-400 text-red-100 ring-red-300'
                                    }`}>
                                        {activeFilterCount}
                                    </div>
                                )}
                            </button>

                            {/* Bulk edit (collection only, owner only) */}
                            {isOwner && !isWishlist && (
                                <button
                                    onClick={toggleBulkMode}
                                    className={`flex items-center gap-3 px-5 py-2.5 rounded-xl transition-all duration-200 font-semibold shadow-md hover:shadow-lg border hover:scale-105 ${
                                        isBulkMode
                                            ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white border-blue-500'
                                            : 'bg-gradient-to-r from-white to-gray-50 hover:from-gray-50 hover:to-gray-100 text-gray-700 border-gray-300'
                                    }`}
                                >
                                    <FontAwesomeIcon icon={faLayerGroup} className={isBulkMode ? 'text-blue-100' : 'text-gray-600'} />
                                    <span>{isBulkMode ? 'Exit Bulk Edit' : 'Bulk Edit'}</span>
                                    {isBulkMode && selectedSets.size > 0 && (
                                        <div className="w-6 h-6 bg-blue-400 bg-opacity-40 text-blue-100 rounded-full text-xs font-bold flex items-center justify-center ring-2 ring-blue-300 ring-opacity-50">
                                            {selectedSets.size}
                                        </div>
                                    )}
                                </button>
                            )}

                            {/* List / Grid toggle */}
                            <button
                                onClick={() => isListView ? setIsListView(false) : switchToListView()}
                                className={`flex items-center gap-2.5 px-5 py-2.5 rounded-xl transition-all duration-200 font-semibold shadow-md hover:shadow-lg border hover:scale-105 ${
                                    isListView
                                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white border-blue-500'
                                        : 'bg-gradient-to-r from-white to-gray-50 hover:from-gray-50 hover:to-gray-100 text-gray-700 border-gray-300'
                                }`}
                                title={isListView ? 'Switch to Grid View' : 'Switch to List View'}
                            >
                                <FontAwesomeIcon icon={isListView ? faGrip : faList} className={isListView ? 'text-blue-100' : 'text-gray-500'} />
                                <span>{isListView ? 'Grid View' : 'List View'}</span>
                            </button>
                        </div>
                    )}
                </div>

                {/* Stats cards (collection only) */}
                {sets.length > 0 && !isWishlist && (
                    <StatsCards sets={sets} showPrices={showPrices} />
                )}

                {/* Filter panel */}
                {sets.length > 0 && showFilters && (
                    <FilterPanel
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        filterOptions={filterOptions}
                        setFilterOptions={setFilterOptions}
                        sortOption={sortOption}
                        isListView={isListView}
                        isWishlist={isWishlist}
                        filteredSets={filteredSets}
                        sets={sets}
                        handleSortChange={handleSortChange}
                        getSortIcon={getSortIcon}
                        resetFilters={resetFilters}
                        getDecadeOptions={getDecadeOptions}
                    />
                )}
            </div>

            {/* Set list — full-width container */}
            {filteredSets.length > 0 ? (
                <div className="max-w-7xl mx-auto mb-4 sm:mb-8">
                    {isListView
                        ? renderListView(themesData)
                        : sortOption === 'theme'
                            ? renderThemeView(themesData)
                            : renderGridView(filteredSets)
                    }
                </div>
            ) : (
                <div className="max-w-7xl mx-auto">
                    {renderEmptyState()}
                </div>
            )}

            {/* Sticky bulk toolbar */}
            {isBulkMode && isOwner && !isWishlist && (
                <BulkToolbar
                    selectedSets={selectedSets}
                    filteredSets={filteredSets}
                    isBulkOpening={isBulkOpening}
                    isBulkRemoving={isBulkRemoving}
                    onSelectAll={selectAllVisible}
                    onDeselectAll={deselectAll}
                    onBulkOpen={handleBulkOpen}
                    onBulkRemoveRequest={() => setBulkConfirmOpen(true)}
                    onExit={toggleBulkMode}
                />
            )}
        </div>
    );
};

export default UserSetsView;
